(() => {
  const NUDGE_SELECTOR = ".size-nudge-cta";
  const CART_LINE_ITEM_SELECTOR = ".js-cart-nudge-item[data-product-id]";

  // How many sizes apart counts as a "jump" worth nudging
  function getNudgeSizeDifference(scope) {
    const nudge = scope.querySelector(NUDGE_SELECTOR);
    return Number(nudge?.dataset.sizeDifference || 1);
  }

  /**
   * Reads a comma-separated list of translated option names off the
   * nudge box, so matching stays correct regardless of storefront locale.
   * @param {Element} scope
   * @param {"sizeOptionNames"|"colorOptionNames"} datasetKey
   * @returns {string[]}
   */
  function getOptionNames(scope, datasetKey) {
    const nudge = scope.querySelector(NUDGE_SELECTOR);
    const raw = nudge?.dataset[datasetKey] || "";
    return raw
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  }

  // The most recent variant/size known to currently be in the cart. Scoped to this module only - not persisted across page loads, since a fresh page load means nothing has been added yet in this session.
  let lastCartVariantId = null;
  let lastCartSize = null;

  /**
   * Reads the currently selected numeric size value within a scope.
   * Tries every translated size-option name until one matches, so it
   * works regardless of storefront locale.
   * Returns null if there's no Size option, nothing selected, or the
   * selected value isn't numeric (e.g. "S"/"M"/"L").
   * @param {Element} scope - Container to search within.
   * @returns {?number}
   */
  function getSizeValue(scope) {
    const sizeOptionNames = getOptionNames(scope, "sizeOptionNames");
    let selector = null;
    for (const name of sizeOptionNames) {
      selector = scope.querySelector(`.option-selector[data-option="${name}"]`);
      if (selector) break;
    }
    if (!selector) return null;

    let raw = null;
    if (selector.dataset.selectorType === "dropdown") {
      const selected = selector.querySelector(
        '.custom-select__option[aria-selected="true"]',
      );
      raw = selected ? selected.dataset.value : null;
    } else {
      const checked = selector.querySelector(".js-option:checked");
      raw = checked ? checked.dataset.size || checked.value : null;
    }

    if (raw === null || raw === "") return null;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Hides every nudge box currently in the DOM.
  function hideAllNudges() {
    document.querySelectorAll(NUDGE_SELECTOR).forEach((nudge) => {
      nudge.hidden = true;
    });
  }

  /**
   * Reveals the matching message variant (size vs. color) within a nudge
   * box and hides the other one.
   * @param {Element} scope - Container to search within.
   * @param {"size"|"color"} reason - Which message to show.
   */
  function setNudgeReason(scope, reason) {
    scope.querySelectorAll(NUDGE_SELECTOR).forEach((nudge) => {
      nudge.querySelectorAll(".nudge-box__content").forEach((content) => {
        content.hidden = content.dataset.reason !== reason;
      });
    });
  }

  /**
   * Shows or hides the nudge box(es) within a given scope.
   * @param {Element} scope - Container to search within (e.g. a product section, or document for the cart page/popup).
   * @param {boolean} show - Whether the nudge should be visible.
   * @param {"size"|"color"} [reason] - Which message to reveal when showing.
   */
  function toggleNudge(scope, show, reason) {
    if (show && reason) setNudgeReason(scope, reason);
    scope.querySelectorAll(NUDGE_SELECTOR).forEach((nudge) => {
      nudge.hidden = !show;
    });
  }

  // ---------------------------------------------------------------------
  // PDP: compares the live variant selection against what's in cart.
  // ---------------------------------------------------------------------

  document.addEventListener("on:cart:add", (evt) => {
    if (!evt.detail || !evt.detail.variantId) return;
    const scope = evt.target.closest(".shopify-section");
    lastCartVariantId = evt.detail.variantId;
    lastCartSize = scope ? getSizeValue(scope) : null;
    hideAllNudges();
  });

  document.addEventListener("on:line-item:change", (evt) => {
    const { newQuantity, oldQuantity } = evt.detail || {};
    if (newQuantity > oldQuantity) hideAllNudges();
    checkCartNudge();
  });

  document.addEventListener("on:variant:change", (evt) => {
    const scope = evt.target.closest(".shopify-section");
    if (!scope || !scope.querySelector(NUDGE_SELECTOR)) return;
    const { variant } = evt.detail || {};
    if (!variant || lastCartVariantId === null) {
      toggleNudge(scope, false);
      return;
    }

    const isDifferentVariant = variant.id !== lastCartVariantId;
    const currentSize = getSizeValue(scope);

    // Sizes aren't numeric (e.g. S/M/L) - fall back to "different variant".
    if (currentSize === null || lastCartSize === null) {
      toggleNudge(scope, isDifferentVariant, "color");
      return;
    }

    const sizeDifference = getNudgeSizeDifference(scope);
    const diff = Math.abs(currentSize - lastCartSize);
    const isSizeJump = Math.abs(diff - sizeDifference) < 0.01;
    const isSameSize = diff < 0.01;

    if (isSizeJump) {
      toggleNudge(scope, true, "size");
    } else if (isSameSize && isDifferentVariant) {
      toggleNudge(scope, true, "color");
    } else {
      toggleNudge(scope, false);
    }
  });

  // ---------------------------------------------------------------------
  // Cart page & cart popup: compare line items already in the cart
  // ---------------------------------------------------------------------

  function normalizeSize(raw) {
    if (raw == null || raw === "") return null;
    const parsed = parseFloat(String(raw).replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * @param {Element|Document} [root] - Container to search within. Defaults to document.
   */
  function checkCartNudge(root = document) {
    const nudge = root.querySelector(NUDGE_SELECTOR);
    if (!nudge) return;

    const rows = Array.from(root.querySelectorAll(CART_LINE_ITEM_SELECTOR));
    const byProduct = new Map();

    rows.forEach((row) => {
      const productId = row.dataset.productId;
      if (!productId) return;
      if (!byProduct.has(productId)) byProduct.set(productId, []);
      byProduct.get(productId).push({
        size: normalizeSize(row.dataset.itemSize),
        color: row.dataset.itemColor || null,
      });
    });

    const sizeDifference = getNudgeSizeDifference(root);
    let reason = null;

    outer: for (const items of byProduct.values()) {
      if (items.length < 2) continue;
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i];
          const b = items[j];
          if (a.size == null || b.size == null) continue;

          const diff = Math.abs(a.size - b.size);
          if (Math.abs(diff - sizeDifference) < 0.01) {
            reason = "size";
            break outer;
          }
          if (diff < 0.01 && a.color && b.color && a.color !== b.color) {
            reason = "color";
            break outer;
          }
        }
      }
    }

    toggleNudge(root, Boolean(reason), reason);
  }

  document.addEventListener("DOMContentLoaded", () => checkCartNudge());

  // Fired by lbrgt-cart-popup.js once it finishes rendering items from
  // /cart.js, since those items exist only in the DOM after that point
  // (unlike the cart page, which has them at initial render via Liquid).
  document.addEventListener("on:cart-popup:rendered", (evt) => {
    checkCartNudge(evt.detail?.root || document);
  });
})();